import React, { useState, useMemo, useEffect, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { Form } from 'react-bootstrap';
import { LuClipboardList, LuFileCheck, LuDownload, LuFileText } from 'react-icons/lu';
import AgregarPackingList from './packinglist/AgregarPackingList';
import AgregarInvoice from './invoice/AgregarInvoice';
import InvoiceFacturaConfirmModal, {
  estadoDestino,
} from './invoice/InvoiceFacturaConfirmModal';
import InvoiceDescargasModal from './invoice/InvoiceDescargasModal';
import { generatePackingListPdf, packingListFromApiToPdfParams } from './packinglist/generatePackingListPDF.js';
import {
  generateInvoicePtcPdf,
  invoicePtcParamsFromApiWithPackingList,
} from './invoice/generateInvoicePtcPDF';
import {
  generateInvoicePtbPdf,
  invoicePtbParamsFromApiWithPackingList,
} from './invoice/generateInvoicePtbPDF';
import { urlApiProd } from './utiles';
import { packingListsSinInvoice } from './invoice/packingListSinInvoiceUtils';
import { invoiceNumberCorrelativo } from './invoice/invoiceNumberUtils';

function fmtFecha(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtNum(v, dec = 3) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '—';
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  }).format(Number(v));
}

/** Una fila por packing list; datos de invoice vacíos si aún no existe. */
function mergePackingListsConInvoices(packingLists, invoices) {
  const byPl = new Map();
  for (const inv of invoices || []) {
    if (inv.id_packing_list != null) byPl.set(inv.id_packing_list, inv);
  }
  return (packingLists || []).map((pl) => {
    const inv = byPl.get(pl.id_packing_list);
    if (inv) {
      return {
        ...inv,
        id_packing_list: pl.id_packing_list,
        nro_booking: inv.nro_booking ?? pl.nro_booking,
        tiene_invoice: true,
        rowKey: `inv-${inv.id_invoice}`,
      };
    }
    return {
      id_invoice: null,
      id_packing_list: pl.id_packing_list,
      numero_invoice: '',
      estado: '',
      fecha_invoice: null,
      nombre_bl: pl.nombre_bl ?? '',
      nro_booking: pl.nro_booking ?? '',
      nombre_cliente: pl.cliente_nombre ?? '',
      total_contenedores: pl.total_contenedores ?? null,
      total_m3: pl.total_volumen_m3 ?? null,
      valor_usd: null,
      contrato_pi: pl.po_label ?? '',
      pais_destino: pl.pais_destino ?? '',
      fecha_creacion: pl.fecha_creacion ?? null,
      fecha_modificacion: pl.fecha_actualizacion ?? pl.fecha_creacion ?? null,
      tiene_invoice: false,
      rowKey: `pl-${pl.id_packing_list}`,
    };
  });
}

function aplicarFiltrosTabla(rows, filtros) {
  const {
    numero,
    mes,
    anio,
    destino,
    cliente,
    pi,
    booking,
    estado,
  } = filtros;
  const fold = (s) =>
    String(s ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '');

  return rows.filter((r) => {
    if (
      numero &&
      !fold(invoiceNumberCorrelativo(r.numero_invoice)).includes(fold(numero)) &&
      !fold(r.numero_invoice).includes(fold(numero))
    ) {
      return false;
    }
    if (booking && !fold(r.nro_booking).includes(fold(booking))) return false;
    if (destino && !fold(r.pais_destino).includes(fold(destino))) return false;
    if (cliente && !fold(r.nombre_cliente).includes(fold(cliente))) return false;
    if (pi && !fold(r.contrato_pi).includes(fold(pi))) return false;
    if (estado) {
      if (!r.tiene_invoice || r.estado !== estado) return false;
    }
    if (mes || anio) {
      if (!r.fecha_invoice) return false;
      const d = new Date(r.fecha_invoice + 'T12:00:00');
      if (Number.isNaN(d.getTime())) return false;
      if (mes && String(d.getMonth() + 1) !== String(parseInt(mes, 10))) return false;
      if (anio && String(d.getFullYear()) !== String(parseInt(anio, 10))) return false;
    }
    return true;
  });
}

const MESES_NOMBRE = [
  { value: '1', label: 'Enero' },
  { value: '2', label: 'Febrero' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Mayo' },
  { value: '6', label: 'Junio' },
  { value: '7', label: 'Julio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

function filtrosExcluyendo(filtros, campo) {
  return { ...filtros, [campo]: '' };
}

function collectOpcionesDesdeFilas(rows) {
  const numeros = new Set();
  const destinos = new Set();
  const clientes = new Set();
  const pis = new Set();
  const bookings = new Set();
  const meses = new Set();
  const anios = new Set();
  for (const r of rows) {
    if (r.numero_invoice) numeros.add(invoiceNumberCorrelativo(r.numero_invoice));
    if (r.pais_destino) destinos.add(String(r.pais_destino));
    if (r.nombre_cliente) clientes.add(String(r.nombre_cliente));
    if (r.contrato_pi) pis.add(String(r.contrato_pi));
    if (r.nro_booking) bookings.add(String(r.nro_booking));
    if (r.fecha_invoice) {
      const d = new Date(r.fecha_invoice + 'T12:00:00');
      if (!Number.isNaN(d.getTime())) {
        meses.add(String(d.getMonth() + 1));
        anios.add(String(d.getFullYear()));
      }
    }
  }
  return {
    numeros: [...numeros].sort((a, b) => a.localeCompare(b, 'es')),
    destinos: [...destinos].sort((a, b) => a.localeCompare(b, 'es')),
    clientes: [...clientes].sort((a, b) => a.localeCompare(b, 'es')),
    pis: [...pis].sort((a, b) => a.localeCompare(b, 'es')),
    bookings: [...bookings].sort((a, b) => a.localeCompare(b, 'es')),
    meses: [...meses].sort((a, b) => Number(a) - Number(b)),
    anios: [...anios].sort((a, b) => Number(b) - Number(a)),
  };
}

/** Opciones de autocompletado según filas ya filtradas por el resto de criterios. */
function buildOpcionesFiltroCascada(base, filtros) {
  const pick = (campo) =>
    collectOpcionesDesdeFilas(aplicarFiltrosTabla(base, filtrosExcluyendo(filtros, campo)));
  return {
    numeros: pick('numero').numeros,
    meses: pick('mes').meses,
    anios: pick('anio').anios,
    destinos: pick('destino').destinos,
    clientes: pick('cliente').clientes,
    pis: pick('pi').pis,
    bookings: pick('booking').bookings,
  };
}

const filterFieldStyle = { flex: '1 1 0', minWidth: 0 };

function FilterAutocomplete({ label, value, onChange, options, placeholder }) {
  const listId = `dl-inv-${label.replace(/\W/g, '')}`;
  return (
    <Form.Group className="mb-0" style={filterFieldStyle}>
      <Form.Label className="small mb-1">{label}</Form.Label>
      <Form.Control
        size="sm"
        className="w-100"
        type="text"
        list={listId}
        autoComplete="off"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <datalist id={listId}>
        {options.map((opt) => (
          <option key={opt} value={opt} />
        ))}
      </datalist>
    </Form.Group>
  );
}

function FilterSelect({ label, value, onChange, options, renderOption, emptyLabel }) {
  return (
    <Form.Group className="mb-0" style={filterFieldStyle}>
      <Form.Label className="small mb-1">{label}</Form.Label>
      <Form.Select size="sm" className="w-100" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{emptyLabel ?? 'Todos'}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {renderOption ? renderOption(opt) : opt}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  );
}

/** Mes: lista completa si no hay fechas en el subconjunto filtrado. */
function opcionesMesSelector(mesesEnDatos) {
  if (mesesEnDatos?.length > 0) return mesesEnDatos;
  return MESES_NOMBRE.map((m) => m.value);
}

/** Año: datos filtrados o ventana alrededor del año actual. */
function opcionesAnioSelector(aniosEnDatos) {
  if (aniosEnDatos?.length > 0) return aniosEnDatos;
  const y = new Date().getFullYear();
  return [String(y), String(y - 1), String(y - 2), String(y - 3)];
}

/** Botón de edición con icono + sigla (PL / INV). */
function BotonEditarAccion({ icon: Icon, label, variant, title, disabled, onClick }) {
  return (
    <button
      type="button"
      className={`btn btn-sm btn-outline-${variant} d-inline-flex align-items-center justify-content-center gap-1 flex-shrink-0`}
      style={{ minWidth: '3.1rem', paddingLeft: '0.45rem', paddingRight: '0.45rem' }}
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon size={15} aria-hidden />
      <span className="fw-semibold" style={{ fontSize: '0.7rem', lineHeight: 1 }}>
        {label}
      </span>
    </button>
  );
}

const btnAccionIconoStyle = { width: '2.35rem', flexShrink: 0 };

const Invoice = () => {
  const [tablaRows, setTablaRows] = useState([]);
  const [tablaRowsBase, setTablaRowsBase] = useState([]);
  const [packingListRows, setPackingListRows] = useState([]);
  const [packingDisponibles, setPackingDisponibles] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [showPackingList, setShowPackingList] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [packingListEditId, setPackingListEditId] = useState(null);
  const [invoiceEditId, setInvoiceEditId] = useState(null);

  const [filtroNumero, setFiltroNumero] = useState('');
  const [filtroMes, setFiltroMes] = useState('');
  const [filtroAnio, setFiltroAnio] = useState('');
  const [filtroDestino, setFiltroDestino] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroPi, setFiltroPi] = useState('');
  const [filtroBooking, setFiltroBooking] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');

  const [confirmFacturaRow, setConfirmFacturaRow] = useState(null);
  const [descargasRow, setDescargasRow] = useState(null);
  const [patchingEstado, setPatchingEstado] = useState(false);
  const [downloadingPl, setDownloadingPl] = useState(false);
  const [downloadingPtc, setDownloadingPtc] = useState(false);
  const [downloadingPtb, setDownloadingPtb] = useState(false);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  }, []);

  const filtrosActuales = useMemo(
    () => ({
      numero: filtroNumero.trim(),
      mes: filtroMes.trim(),
      anio: filtroAnio.trim(),
      destino: filtroDestino.trim(),
      cliente: filtroCliente.trim(),
      pi: filtroPi.trim(),
      booking: filtroBooking.trim(),
      estado: filtroEstado.trim(),
    }),
    [
      filtroNumero,
      filtroMes,
      filtroAnio,
      filtroDestino,
      filtroCliente,
      filtroPi,
      filtroBooking,
      filtroEstado,
    ]
  );

  const fetchTabla = useCallback(async () => {
    const headers = authHeaders();
    if (!headers) {
      setLoadingList(false);
      setTablaRows([]);
      setTablaRowsBase([]);
      return;
    }
    setLoadingList(true);
    try {
      const [plRes, invRes] = await Promise.all([
        fetch(`${urlApiProd}/packinglists`, { headers }),
        fetch(`${urlApiProd}/commercialinvoices`, { headers }),
      ]);
      const pl = plRes.ok ? await plRes.json() : [];
      const inv = invRes.ok ? await invRes.json() : [];
      setPackingDisponibles(
        packingListsSinInvoice(
          Array.isArray(pl) ? pl : [],
          Array.isArray(inv) ? inv : []
        )
      );
      setPackingListRows(pl);
      const merged = mergePackingListsConInvoices(pl, inv);
      setTablaRowsBase(merged);
    } catch (e) {
      console.error('Error cargando vista invoice:', e);
    } finally {
      setLoadingList(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchTabla();
  }, [fetchTabla]);

  useEffect(() => {
    setTablaRows(aplicarFiltrosTabla(tablaRowsBase, filtrosActuales));
  }, [tablaRowsBase, filtrosActuales]);

  const opcionesFiltro = useMemo(
    () => buildOpcionesFiltroCascada(tablaRowsBase, filtrosActuales),
    [tablaRowsBase, filtrosActuales]
  );

  const mesesSelector = useMemo(
    () => opcionesMesSelector(opcionesFiltro.meses),
    [opcionesFiltro.meses]
  );

  const aniosSelector = useMemo(
    () => opcionesAnioSelector(opcionesFiltro.anios),
    [opcionesFiltro.anios]
  );

  const existingBookingNumbers = useMemo(
    () => packingListRows.map((r) => String(r.nro_booking || '').trim()).filter(Boolean),
    [packingListRows]
  );

  const existingBlPorInvoice = useMemo(
    () =>
      packingListRows
        .filter((r) => r.nro_booking && r.nombre_bl)
        .map((r) => ({
          nro_booking: String(r.nro_booking).trim(),
          nombre_bl: String(r.nombre_bl).trim(),
        })),
    [packingListRows]
  );

  const handleDownloadInvoicePtb = async (row) => {
    const headers = authHeaders();
    if (!row?.id_invoice || !headers) {
      alert('Se debe crear la invoice (prefactura) antes de descargar el PDF PTB.');
      return;
    }
    setDownloadingPtb(true);
    try {
      const invRes = await fetch(`${urlApiProd}/commercialinvoices/${row.id_invoice}`, {
        headers,
      });
      if (!invRes.ok) {
        const err = await invRes.json().catch(() => ({}));
        alert(err.error || 'No se pudo cargar la invoice.');
        return;
      }
      const inv = await invRes.json();
      const params = await invoicePtbParamsFromApiWithPackingList(inv, {
        apiBase: urlApiProd,
        headers,
      });
      generateInvoicePtbPdf(params);
    } catch (e) {
      alert(e.message || 'Error de red');
    } finally {
      setDownloadingPtb(false);
    }
  };

  const handleDownloadInvoicePtc = async (row) => {
    const headers = authHeaders();
    if (!row?.id_invoice || !headers) {
      alert('Se debe crear la invoice (prefactura) antes de descargar el PDF PTC.');
      return;
    }
    setDownloadingPtc(true);
    try {
      const invRes = await fetch(`${urlApiProd}/commercialinvoices/${row.id_invoice}`, {
        headers,
      });
      if (!invRes.ok) {
        const err = await invRes.json().catch(() => ({}));
        alert(err.error || 'No se pudo cargar la invoice.');
        return;
      }
      const inv = await invRes.json();
      const params = await invoicePtcParamsFromApiWithPackingList(inv, {
        apiBase: urlApiProd,
        headers,
      });
      generateInvoicePtcPdf(params);
    } catch (e) {
      alert(e.message || 'Error de red');
    } finally {
      setDownloadingPtc(false);
    }
  };

  const handleDownloadPackingList = async (row) => {
    const headers = authHeaders();
    if (!row?.id_packing_list || !headers) return;
    setDownloadingPl(true);
    try {
      const res = await fetch(`${urlApiProd}/packinglists/${row.id_packing_list}`, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'No se pudo cargar el packing list.');
        return;
      }
      generatePackingListPdf(packingListFromApiToPdfParams(await res.json()));
    } catch (e) {
      alert(e.message || 'Error de red');
    } finally {
      setDownloadingPl(false);
    }
  };

  const hintCambioEstadoInvoice = (row) => {
    if (!row?.tiene_invoice) return 'Se debe crear prefactura';
    if (row.estado === 'factura') return 'Volver a prefactura';
    return 'Cambiar a factura';
  };

  const confirmarCambioEstadoInvoice = async (numeroInvoice) => {
    const row = confirmFacturaRow;
    const headers = authHeaders();
    const nuevoEstado = estadoDestino(row);
    if (!row || !headers || !nuevoEstado) return;
    const numero = invoiceNumberCorrelativo(numeroInvoice).trim();
    if (nuevoEstado === 'factura' && !numero) {
      alert('Ingrese el N° de invoice.');
      return;
    }
    const payload = { estado: nuevoEstado, numero_invoice: numero };
    setPatchingEstado(true);
    try {
      const res = await fetch(`${urlApiProd}/commercialinvoices/${row.id_invoice}/estado`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'No se pudo actualizar el estado.');
        return;
      }
      setConfirmFacturaRow(null);
      fetchTabla();
    } catch (e) {
      alert(e.message || 'Error de red');
    } finally {
      setPatchingEstado(false);
    }
  };

  const columns = [
    {
      name: 'N° Invoice',
      selector: (r) => invoiceNumberCorrelativo(r.numero_invoice),
      sortable: true,
      grow: 1,
      cell: (r) => invoiceNumberCorrelativo(r.numero_invoice) || '—',
    },
    {
      name: 'Estado',
      width: '120px',
      selector: (r) => r.estado ?? '',
      sortable: true,
      cell: (r) => {
        if (!r.tiene_invoice) {
          return <span className="badge bg-secondary">Sin invoice</span>;
        }
        return (
          <span
            className={
              r.estado === 'factura' ? 'badge bg-success' : 'badge bg-warning text-dark'
            }
          >
            {r.estado === 'factura' ? 'Factura' : 'Prefactura'}
          </span>
        );
      },
    },
    {
      name: 'Fecha Invoice',
      selector: (r) => r.fecha_invoice ?? '',
      sortable: true,
      width: '110px',
      cell: (r) => r.fecha_invoice || '—',
    },
    { name: 'BL', selector: (r) => r.nombre_bl ?? '', sortable: true, width: '100px' },
    { name: 'Booking', selector: (r) => r.nro_booking ?? '', sortable: true, width: '120px' },
    {
      name: 'Consignee',
      selector: (r) => r.nombre_cliente ?? '',
      sortable: true,
      grow: 1.2,
    },
    {
      name: 'Cant HC',
      selector: (r) => r.total_contenedores ?? '',
      sortable: true,
      width: '95px',
      cell: (r) => fmtNum(r.total_contenedores, 0),
    },
    {
      name: 'M³',
      selector: (r) => r.total_m3 ?? '',
      sortable: true,
      width: '90px',
      cell: (r) => fmtNum(r.total_m3, 3),
    },
    {
      name: 'Valor USD',
      selector: (r) => r.valor_usd ?? '',
      sortable: true,
      width: '100px',
      cell: (r) => (r.valor_usd != null && r.valor_usd !== '' ? fmtNum(r.valor_usd, 2) : '—'),
    },
    {
      name: 'Contrato/PI',
      selector: (r) => r.contrato_pi ?? '',
      sortable: true,
      grow: 1,
    },
    {
      name: 'Creación / modificación',
      selector: (r) => r.fecha_modificacion ?? r.fecha_creacion ?? '',
      sortable: true,
      width: '160px',
      cell: (r) =>
        r.fecha_modificacion || r.fecha_creacion ? (
          <span className="small" title={`Creado: ${fmtFecha(r.fecha_creacion)}`}>
            {fmtFecha(r.fecha_modificacion || r.fecha_creacion)}
          </span>
        ) : (
          '—'
        ),
    },
    {
      name: 'Acciones',
      width: '248px',
      minWidth: '248px',
      cell: (row) => (
        <div className="d-flex flex-nowrap align-items-center gap-2 py-1">
          <div className="d-flex flex-nowrap align-items-center gap-1">
            <BotonEditarAccion
              icon={LuClipboardList}
              label="PL"
              variant="info"
              title="Editar packing list"
              onClick={() => {
                setPackingListEditId(row.id_packing_list);
                setShowPackingList(true);
              }}
            />
            <BotonEditarAccion
              icon={LuFileText}
              label="INV"
              variant={row.tiene_invoice ? 'primary' : 'secondary'}
              title={row.tiene_invoice ? 'Editar invoice' : 'Sin invoice — crear invoice primero'}
              disabled={!row.tiene_invoice}
              onClick={() => {
                if (!row.tiene_invoice) return;
                setInvoiceEditId(row.id_invoice);
                setShowInvoice(true);
              }}
            />
          </div>
          <div
            className="d-flex flex-nowrap align-items-center gap-1"
            style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '0.35rem' }}
          >
            <span
              className="d-inline-block"
              title={hintCambioEstadoInvoice(row)}
              style={!row.tiene_invoice ? { cursor: 'not-allowed' } : undefined}
            >
              <button
                type="button"
                className={`btn btn-sm flex-shrink-0 ${
                  row.tiene_invoice && row.estado === 'factura'
                    ? 'btn-outline-warning'
                    : 'btn-outline-success'
                }`}
                style={btnAccionIconoStyle}
                disabled={!row.tiene_invoice}
                title={hintCambioEstadoInvoice(row)}
                aria-label={hintCambioEstadoInvoice(row)}
                onClick={() => row.tiene_invoice && setConfirmFacturaRow(row)}
              >
                <LuFileCheck size={16} aria-hidden />
              </button>
            </span>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm flex-shrink-0"
              style={btnAccionIconoStyle}
              title="Descargar documentos"
              aria-label="Descargar documentos"
              onClick={() => setDescargasRow(row)}
            >
              <LuDownload size={16} aria-hidden />
            </button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="container-fluid">
      <AgregarPackingList
        show={showPackingList}
        cerrar={() => {
          setShowPackingList(false);
          setPackingListEditId(null);
        }}
        packingListEditId={packingListEditId}
        existingBookingNumbers={existingBookingNumbers}
        existingBlPorInvoice={existingBlPorInvoice}
        onGuardarExito={() => {
          fetchTabla();
        }}
      />
      <AgregarInvoice
        show={showInvoice}
        cerrar={() => {
          setShowInvoice(false);
          setInvoiceEditId(null);
        }}
        invoiceEditId={invoiceEditId}
        packingListDisponibles={packingDisponibles}
        onGuardarExito={() => {
          fetchTabla();
        }}
      />

      <InvoiceFacturaConfirmModal
        show={!!confirmFacturaRow}
        row={confirmFacturaRow}
        onClose={() => setConfirmFacturaRow(null)}
        onConfirm={confirmarCambioEstadoInvoice}
        loading={patchingEstado}
      />
      <InvoiceDescargasModal
        show={!!descargasRow}
        row={descargasRow}
        onClose={() => setDescargasRow(null)}
        onDownloadPackingList={handleDownloadPackingList}
        onDownloadInvoicePtc={handleDownloadInvoicePtc}
        onDownloadInvoicePtb={handleDownloadInvoicePtb}
        downloadingPl={downloadingPl}
        downloadingPtc={downloadingPtc}
        downloadingPtb={downloadingPtb}
      />

      <div className="card">
        <div className="card-header">
          <h3 className="card-title mb-0" style={{ textAlign: 'center' }}>
            Invoice
          </h3>
        </div>
        <div className="card-body mb-2">
          <div className="input-group mb-3 d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => {
                setPackingListEditId(null);
                setShowPackingList(true);
              }}
            >
              Crear Packing list
            </button>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => {
                setInvoiceEditId(null);
                setShowInvoice(true);
              }}
            >
              Crear Invoice
            </button>
          </div>

          <div className="card mb-3">
            <div className="card-body py-3">
              <div className="d-flex flex-nowrap gap-2 align-items-end w-100">
                <FilterAutocomplete
                  label="N° Invoice"
                  value={filtroNumero}
                  onChange={setFiltroNumero}
                  options={opcionesFiltro.numeros}
                  placeholder="Buscar…"
                />
                <FilterSelect
                  label="Mes"
                  value={filtroMes}
                  onChange={setFiltroMes}
                  options={mesesSelector}
                  emptyLabel="Cualquier mes"
                  renderOption={(m) => MESES_NOMBRE.find((x) => x.value === m)?.label || m}
                />
                <FilterSelect
                  label="Año"
                  value={filtroAnio}
                  onChange={setFiltroAnio}
                  options={aniosSelector}
                  emptyLabel="Cualquier año"
                />
                <FilterAutocomplete
                  label="País destino"
                  value={filtroDestino}
                  onChange={setFiltroDestino}
                  options={opcionesFiltro.destinos}
                  placeholder="Destino…"
                />
                <FilterAutocomplete
                  label="Consignee"
                  value={filtroCliente}
                  onChange={setFiltroCliente}
                  options={opcionesFiltro.clientes}
                  placeholder="Consignee…"
                />
                <FilterAutocomplete
                  label="PI"
                  value={filtroPi}
                  onChange={setFiltroPi}
                  options={opcionesFiltro.pis}
                  placeholder="PI…"
                />
                <FilterAutocomplete
                  label="Booking"
                  value={filtroBooking}
                  onChange={setFiltroBooking}
                  options={opcionesFiltro.bookings}
                  placeholder="Booking…"
                />
                <FilterSelect
                  label="Estado"
                  value={filtroEstado}
                  onChange={setFiltroEstado}
                  options={['prefactura', 'factura']}
                  emptyLabel="Cualquier estado"
                  renderOption={(v) => (v === 'factura' ? 'Factura' : 'Prefactura')}
                />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              {loadingList ? (
                <p className="text-muted text-center mb-0">Cargando…</p>
              ) : (
                <DataTable
                  keyField="rowKey"
                  columns={columns}
                  data={tablaRows}
                  noHeader
                  responsive
                  pagination
                  highlightOnHover
                  noDataComponent={
                    <div className="p-4 text-muted text-center">No hay packing lists</div>
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoice;
